import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Download } from 'lucide-react';
import { useExamReport, useStudentsList, useTeacherAssignments } from '../../api/hooks';
import type { ExamReportResponse } from '../../api/types';
import SectionPicker from '../../components/SectionPicker';
import { PanelCard, EmptyState, Spinner, ErrorPanel, PageHeader, Tabs, Button, Tag, Term } from '../../components/ui';
import { FaClipboardList } from '../../components/ui/icons';
import ReportHeader from './components/ReportHeader';
import ReportQuestionsTable from './components/ReportQuestionsTable';
import ReportChaptersBlock from './components/ReportChaptersBlock';
import ScoreDistributionCard from './components/ScoreDistributionCard';
import MostMissedCard from './components/MostMissedCard';
import ResultsReleaseCard from './components/ResultsReleaseCard';
import SectionsComparedCard, { sectionTeacher } from './components/SectionsComparedCard';
import ReportStudentsTable from './components/ReportStudentsTable';
import StartedNotSubmitted from './components/StartedNotSubmitted';
import RemediateDialog from '../class_performance/components/RemediateDialog';
import { DEFAULT_SCOPES } from '../../lib/scopes';
import type { GlossaryKey } from '../../lib/glossary';
import CoachLaunchButton from '../student_360/coach/CoachLaunchButton';

type TabKey = 'questions' | 'students';

/**
 * Per-chapter percentage for one student, from the report's below_pass lists.
 * A student who is not listed is either above the pass mark or never took the
 * chapter's questions — either way the report does not carry their number, so
 * it stays null (rendered as "—") rather than being invented from pass_score_pct.
 */
function chapterPcts(d: ExamReportResponse, studentId: number): Record<number, number | null> {
  return Object.fromEntries(d.chapters.map((c) => {
    const hit = c.below_pass.find((b) => b.student_id === studentId);
    return [c.chapter_id, hit ? hit.pct : null];
  }));
}

export default function ExamReportPage() {
  const { examId } = useParams<{ examId: string }>();
  const id = examId ? Number(examId) : null;
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const initial = sp.get('section');
  // A hand-edited ?section=abc must not become NaN and poison the query key.
  const [section, setSection] = useState<number | 'all'>(
    initial && initial !== 'all' && Number.isFinite(Number(initial)) ? Number(initial) : 'all');
  const rawTab = sp.get('tab');
  const tab: TabKey = rawTab === 'students' ? 'students' : 'questions';
  const setTab = (t: TabKey) => {
    const next = new URLSearchParams(sp);
    if (t === 'questions') next.delete('tab'); else next.set('tab', t);
    setSp(next, { replace: true });
  };
  const q = useExamReport(id, { section });
  // The whole-grade report carries the per-section figures; a section-filtered one does not, so the
  // "Sections compared" card keeps reading the all-sections report (same cache entry when section is all).
  const allQ = useExamReport(id, { section: 'all' });
  const teachersQ = useTeacherAssignments({ retry: false });
  const [suspectOnly, setSuspectOnly] = useState(false);
  const [remediate, setRemediate] = useState<number[] | null>(null);
  const chapterName = useMemo(() => {
    const m: Record<number, string> = {};
    (q.data?.chapters ?? []).forEach((c) => { m[c.chapter_id] = c.chapter_name; });
    return m;
  }, [q.data]);

  // The remediation pool is the whole cohort, not just this exam's takers: a
  // student who missed the exam is exactly the one a teacher may want to add.
  // Only fetched while the dialog is open.
  const cohortGrade = q.data?.exam.grade?.id ?? null;
  const cohortQ = useStudentsList(
    { ...(cohortGrade != null ? { grade: cohortGrade } : {}), ...(section !== 'all' ? { section } : {}) },
    { enabled: remediate != null && cohortGrade != null },
  );

  if (q.isLoading) return <div className="flex justify-center py-8"><Spinner size="md" /></div>;
  if (q.isError || !q.data) return <ErrorPanel />;
  const d = q.data;
  const tabs: Array<{ key: TabKey; label: string; count: number; help: GlossaryKey }> = [
    { key: 'questions', label: 'Item analysis', count: d.questions.length, help: 'tab_report_questions' },
    { key: 'students', label: 'Students', count: d.students.filter((x) => !x.awaiting_marking).length, help: 'tab_report_students' },
  ];
  const subject = d.exam.subject?.name ?? '\u2014';
  // Grade names may already read "Grade 10": never print "Grade Grade 10".
  const gradeNum = (d.exam.grade?.name ?? '').trim().replace(/^grade\s+/i, '');
  const gradeLabel = gradeNum ? `Grade ${gradeNum}` : '\u2014';
  const sections = allQ.data?.header.sections ?? [];
  const sectionName = section === 'all' ? null : (sections.find((x) => x.id === section)?.name ?? String(section));
  const sectionLabel = sectionName == null ? 'all sections' : `Section ${sectionName}`;
  const suspectCount = d.questions.filter((x) => x.suspect).length;
  const reviewSuspect = () => {
    const next = new URLSearchParams(sp);
    next.set('tab', 'questions');
    setSp(next, { replace: true });
    setSuspectOnly(true);
  };
  return (
    <div className="space-y-[18px]">
      <PageHeader
        eyebrow={<><Link className="hover:text-ink" to="/exams/exam-list">Exam list</Link> / <Link className="hover:text-ink" to={`/exams/exam-detail/${d.exam.id}`}>{d.exam.name}</Link> / Report</>}
        title={d.exam.name}
        subtitle={
          <>
            {`${subject} \u00b7 ${gradeLabel} \u00b7 pass mark ${d.header.pass_score_pct}% \u00b7 ${sectionLabel} \u00b7 ${d.header.took_count} of ${d.header.assigned_count} took${(d.header.late_count ?? 0) > 0 ? ` (+${d.header.late_count} late)` : ''}`}
            <span className="mt-3 flex flex-wrap items-center gap-1.5">
              <Tag>{`${gradeLabel} \u00b7 ${subject}`}</Tag>
              {d.exam.scope !== 'school' && <Tag>{d.exam.scope}</Tag>}
              {d.exam.is_generated && <Tag>AI generated</Tag>}
              <Tag>{d.exam.delivery === 'paper' ? <Term k="delivery">Paper</Term> : 'Online'}</Tag>
            </span>
          </>
        }
        actions={
          <>
            <SectionPicker aria-label="Section"
              grade={d.exam.grade?.id ?? null} value={section} onChange={setSection} />
            {d.exam.delivery === 'paper' && (
              <Button variant="ghost" size="md" onClick={() => navigate(`/exams/paper/${d.exam.id}/sheets`)}>
                Scan sheets
              </Button>
            )}
            <Link to={`/exams/exam-detail/${d.exam.id}`}
              className="inline-flex min-h-[44px] items-center justify-center rounded-[12px] border border-line-strong bg-surface px-4 py-2 text-sm font-semibold text-ink transition hover:bg-surface-2 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent">
              Open exam
            </Link>
            <CoachLaunchButton inline label="Ask coach about this exam" scope={{ type: 'exam', id: d.exam.id, label: d.exam.name }} />
            <Button icon={<Download size={18} strokeWidth={1.8} aria-hidden="true" />} onClick={() => window.print()}>Export report</Button>
          </>
        }
      />
      {d.results_release && (
        <ResultsReleaseCard examId={d.exam.id} dueAt={d.exam.due_at} awaitingCount={d.header.awaiting_marking_count ?? 0}
          report={{ results_release: d.results_release, results_released_at: d.results_released_at ?? null, released: !!d.released }} />
      )}
      {d.header.took_count === 0 ? (
        (d.header.awaiting_marking_count ?? 0) > 0 || (d.header.late_count ?? 0) > 0 ? (
          // Everyone who took it is still waiting for written answers to be marked,
          // or only late submissions (never counted) are in.
          <PanelCard>
            {(d.header.awaiting_marking_count ?? 0) > 0 ? (
              <EmptyState icon={<FaClipboardList />} title="No marked results yet"
                body="Scores appear here once the written answers are marked." />
            ) : (
              <EmptyState icon={<FaClipboardList />} title="No counted results yet"
                body="Only late submissions so far. They are listed below but not counted in class averages." />
            )}
            <div className="mt-4">
              <ReportStudentsTable rows={d.students} chapterName={chapterName} examId={d.exam.id} />
            </div>
            <StartedNotSubmitted items={d.started_not_submitted} />
          </PanelCard>
        ) : (
          <PanelCard>
            <EmptyState icon={<FaClipboardList />} title="No results yet" body="Nobody in this cohort has taken this exam." />
            <StartedNotSubmitted items={d.started_not_submitted} />
          </PanelCard>
        )
      ) : (
        <>
          <ReportHeader h={d.header} />
          <div className={`grid gap-[18px] ${sections.length > 0 ? 'xl:grid-cols-[2fr_1fr]' : ''}`}>
            <ScoreDistributionCard students={d.students} passPct={d.header.pass_score_pct} sectionLabel={sectionLabel} />
            {sections.length > 0 && (
              <SectionsComparedCard
                sections={sections}
                selected={section}
                onSelect={setSection}
                gradeAvg={allQ.data?.header.avg_score ?? null}
                teacherOf={(sid) => sectionTeacher(teachersQ.data ?? [], d.exam.grade?.id ?? null, d.exam.subject?.id ?? null, sid)}
              />
            )}
          </div>
          <div className="grid gap-[18px] xl:grid-cols-[1fr_2fr]">
            <MostMissedCard questions={d.questions} suspectCount={suspectCount} onReviewSuspect={reviewSuspect} />
            <ReportChaptersBlock chapters={d.chapters} passPct={d.header.pass_score_pct} onRemediate={setRemediate} />
          </div>
          <PanelCard aria-label="Item analysis and student results">
            <Tabs<TabKey> accent items={tabs} value={tab} onChange={setTab} />
            {tab === 'questions' && <ReportQuestionsTable rows={d.questions} suspectOnly={suspectOnly} onSuspectOnlyChange={setSuspectOnly} examId={d.exam.id} />}
            {tab === 'students' && <><ReportStudentsTable rows={d.students} chapterName={chapterName} examId={d.exam.id} /><StartedNotSubmitted items={d.started_not_submitted} /></>}
          </PanelCard>
        </>
      )}
      {remediate && d.exam.grade && d.exam.subject && !cohortQ.isLoading && (
        <RemediateDialog
          key={remediate.join(',')}
          isOpen onClose={() => setRemediate(null)}
          cohort={{ grade_id: d.exam.grade.id, section_id: section === 'all' ? null : section,
                    subject_id: d.exam.subject.id, scopes: [...DEFAULT_SCOPES], source_exam_id: d.exam.id }}
          chapters={d.chapters.filter((c) => remediate.includes(c.chapter_id)).map((c) => ({ chapter_id: c.chapter_id, name: c.chapter_name }))}
          candidates={(cohortQ.data?.results ?? []).length > 0
            ? cohortQ.data!.results.map((row) => ({
              student_id: row.id,
              name: `${row.first_name ?? ''} ${row.last_name ?? ''}`.trim() || row.username,
              pct_by_chapter: chapterPcts(d, row.id),
            }))
            // Cohort unavailable (error / empty): fall back to this exam's takers.
            : d.students.map((s) => ({
              student_id: s.student_id, name: s.name, pct_by_chapter: chapterPcts(d, s.student_id),
            }))}
          passPct={d.header.pass_score_pct}
        />
      )}
    </div>
  );
}
